<template>
  <div class="commission-editor">
    <legend>
      <strong>{{ $t('commissionByDepth') }}</strong>
    </legend>

    <!-- Distribution Mode Switch -->
    <b-form-group
      horizontal
      :label="$t('distributionMethod')"
      label-for="distributionMode"
      label-class="text-md-right"
      :label-cols="4"
      breakpoint="md"
    >
      <SelectButton
        id="distributionMode"
        v-model="localDistributionMode"
        :options="distributionModeOptions"
      >
        <template #option="props">
          {{ $t(props.option) }}
        </template>
      </SelectButton>
    </b-form-group>

    <!-- Overall Commission Input -->
    <b-form-group
      horizontal
      :label="$t('totalCommission')"
      label-for="totalCommission"
      label-class="text-md-right"
      :label-cols="4"
      breakpoint="md"
    >
      <InputNumber
        v-model.number="totalCommission"
        mode="decimal"
        :minFractionDigits="1"
        :maxFractionDigits="2"
        :min="0"
        :max="100"
        :placeholder="$t('totalCommission')"
        suffix=" %"
        locale="de-DE"
        class="w-100"
      />
    </b-form-group>

    <!-- Commission Inputs for Each Level -->
    <div v-if="localCommissionByDepth.length > 0">
      <b-form-group
        v-for="(commission, index) in localCommissionByDepth"
        :key="index"
        horizontal
        :label="`Ebene ${index + 1}`"
        :label-for="`commission-${index}`"
        label-class="text-md-right"
        :label-cols="4"
        breakpoint="md"
      >
        <InputNumber
          :id="`commission-${index}`"
          v-model.number="localCommissionByDepth[index]"
          mode="decimal"
          :minFractionDigits="1"
          :maxFractionDigits="2"
          :min="0"
          :max="100"
          :placeholder="`Ebene ${index + 1}`"
          suffix=" %"
          locale="de-DE"
          class="w-100"
          :disabled="localDistributionMode === 'AUTOMATIC'"
          @input="onCommissionChange"
        />
      </b-form-group>
    </div>

    <!-- Sum Validation Display -->
    <b-form-group
      v-if="!isValid"
      horizontal
      label=""
      label-for="sumValidation"
      label-class="text-md-right"
      :label-cols="4"
      breakpoint="md"
    >
      <span class="text-danger">
        {{ $t('sum') }}:{{ sumOfPercentages.toFixed(2) }}% ({{ $t('target') }}:
        {{ totalCommission.toFixed(2) }}%)</span
      >
    </b-form-group>
  </div>
</template>

<script>
import InputNumber from 'primevue/inputnumber';
import SelectButton from 'primevue/selectbutton';

export default {
  name: 'CommissionEditor',
  components: {
    InputNumber,
    SelectButton,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    distributionMode: {
      type: String,
      required: true,
      validator: (value) => ['AUTOMATIC', 'MANUAL'].includes(value),
    },
    currentDepth: {
      type: Number,
      default: 3,
    },
  },
  data() {
    return {
      distributionModeOptions: ['AUTOMATIC', 'MANUAL'],
      localDistributionMode: this.distributionMode, // Local copy of prop
      totalCommission: 10,
      localCommissionByDepth: [], // Stored as percentages for display
      isSyncingFromParent: false, // Flag to prevent emit loop when syncing from parent
    };
  },

  watch: {
    distributionMode: {
      handler(newVal) {
        // Sync prop changes to local copy
        this.localDistributionMode = newVal;
        if (newVal === 'AUTOMATIC') {
          this.onDistributionModeChange();
        }
      },
      immediate: true,
    },
    localDistributionMode: {
      handler(newVal, oldVal) {
        console.log('localDistributionMode watcher:', { newVal, oldVal });
        // Only emit if value actually changed (avoid initial sync)
        if (oldVal !== undefined && newVal !== oldVal) {
          console.log('Emitting update:distributionMode', newVal);
          // Emit changes to parent
          this.$emit('update:distributionMode', newVal);
        }
        if (newVal === 'AUTOMATIC') {
          this.onDistributionModeChange();
        }
      },
    },
    currentDepth: {
      handler(newVal) {
        if (this.localDistributionMode === 'AUTOMATIC') {
          this.onDistributionModeChange();
        } else {
          // In manual mode, just adjust array length
          this.onDepthChange();
        }
      },
    },
    totalCommission: {
      handler(newVal) {
        if (this.localDistributionMode === 'AUTOMATIC') {
          this.onDistributionModeChange();
        }
      },
    },
    localCommissionByDepth: {
      deep: true,
      handler() {
        // Auto-emit when commission values change (but not when syncing from parent)
        if (!this.isSyncingFromParent) {
          // Convert percentages to decimals for model (2 -> 0.02)
          const decimals = this.localCommissionByDepth.map((val) => (val || 0) / 100);
          this.$emit('input', decimals);
        }
      },
    },
    isValid: {
      handler(newVal) {
        this.$emit('update:isValid', newVal);
      },
      immediate: true,
    },
  },

  computed: {
    /**
     * Calculate the sum of all commission percentages
     */
    sumOfPercentages() {
      return this.localCommissionByDepth.reduce((sum, val) => sum + (val || 0), 0);
    },
    /**
     * Check if the commission sum is valid (matches target in MANUAL mode)
     */
    isValid() {
      if (this.localDistributionMode === 'AUTOMATIC') {
        return true; // Always valid in automatic mode
      }
      const diff = Math.abs(this.sumOfPercentages - this.totalCommission);
      return diff <= 0.001; // Allow small floating point differences
    },
  },

  methods: {
    /**
     * Calculate bottom-heavy commission distribution across hierarchy levels.
     * Lower levels (higher indices) receive more commission.
     * Uses predefined weights for common depths (2-5) that produce integer values.
     *
     * @param {number} totalCommission - Total commission percentage (e.g., 10 for 10%)
     * @param {number} depth - Number of hierarchy levels
     * @returns {number[]} Array of integer commission percentages per level (e.g., [2, 3, 5])
     */
    calculateBottomHeavyCommission(totalCommission, depth) {
      if (depth <= 0 || !totalCommission || totalCommission <= 0) {
        return [];
      }

      // Predefined weights for common depths that produce integer values
      // Weights are chosen so their sum divides evenly into common totals (10, 15, 20, etc.)
      // Lower levels (higher indices) get higher weights (bottom-heavy distribution)
      const weightMap = {
        2: [2, 3], // Sum = 5 → 10% gives [4, 6]
        3: [2, 3, 5], // Sum = 10 → 10% gives [2, 3, 5]
        4: [1, 2, 3, 4], // Sum = 10 → 10% gives [1, 2, 3, 4]
        5: [1, 2, 2, 2, 3], // Sum = 10 → 10% gives [1, 2, 2, 2, 3]
      };

      // Get weights for the specified depth, or fall back to default calculation
      let weights;
      if (weightMap[depth]) {
        weights = weightMap[depth];
      } else {
        // Fallback: use sequential weights for depths > 5
        weights = [];
        for (let i = 0; i < depth; i++) {
          weights.push(i + 1);
        }
      }

      // Sum of all weights
      const sumWeights = weights.reduce((sum, w) => sum + w, 0);

      // Calculate commission for each level
      // Since weights are chosen to produce integers, results will be integers
      const commissions = weights.map((weight) => {
        return (totalCommission * weight) / sumWeights;
      });

      // Round to ensure integers (should already be integers with predefined weights)
      return commissions.map((c) => Math.round(c));
    },
    onDistributionModeChange() {
      if (this.localDistributionMode === 'AUTOMATIC') {
        this.localCommissionByDepth = this.calculateBottomHeavyCommission(
          this.totalCommission,
          this.currentDepth
        );
      }
    },
    onDepthChange() {
      // Adjust array length when depth changes
      if (this.currentDepth > this.localCommissionByDepth.length) {
        // Add new levels with 0
        while (this.localCommissionByDepth.length < this.currentDepth) {
          this.localCommissionByDepth.push(0);
        }
      } else if (this.currentDepth < this.localCommissionByDepth.length) {
        // Remove excess levels
        this.localCommissionByDepth = this.localCommissionByDepth.slice(0, this.currentDepth);
      }
    },
    onCommissionChange() {
      // Handled by watcher
    },
  },
  mounted() {
    // Initialize from prop value if available
    if (this.value && Array.isArray(this.value) && this.value.length > 0) {
      // Convert decimals to percentages for UI (0.02 -> 2)
      this.localCommissionByDepth = this.value.map((val) => (val || 0) * 100);
    } else {
      // If in AUTOMATIC mode and no value from parent, calculate and emit
      if (this.localDistributionMode === 'AUTOMATIC') {
        this.onDistributionModeChange();
      }
    }
  },
};
</script>

<style scoped lang="scss">
.commission-editor {
  margin-bottom: 1rem;
}

legend {
  position: relative;
}
</style>
